import { oneOnboardingApi } from "libs/api";
import { useCallback, useState } from "react";
import { useParams } from "router";
import type { PhotoUploadProps } from "./PhotoUpload";

export type UploadQueue = Record<string, PhotoUploadProps>;

interface Options {
    onSuccess: (photoId: number, src: string) => void;
    onError: () => void;
}

export const useUploadPhotoHandler = ({ onSuccess, onError }: Options) => {
    const { lang, propertyId } = useParams("/:lang/list-my-property/onboard/:propertyId/photos");
    const [uploadQueue, setUploadQueue] = useState<UploadQueue>({});

    const upsertStatus = (key: string, progress: number, src?: string) => {
        setUploadQueue((prev) => {
            let updatedQueue = { ...prev };

            if (progress === -1) delete updatedQueue[key];
            else
                updatedQueue[key] = {
                    src: src!,
                    percentage: progress,
                };

            return updatedQueue;
        });
    };

    const upload = useCallback(
        (file: File) => {
            // set initial state
            const blobString = URL.createObjectURL(file);
            upsertStatus(blobString, 0, blobString);

            // call api to upload
            oneOnboardingApi
                .uploadPhoto(lang, Number(propertyId), file, {
                    onUploadProgress: (event) => {
                        // update upload progress on queue
                        upsertStatus(blobString, Math.round((event.loaded * 100) / event.total), blobString);
                    },
                })
                .then((res) => {
                    if (res.data.errorType) return onError();
                    else return onSuccess(res.data.id, blobString);
                })
                .catch(() => {
                    onError();
                })
                .finally(() => {
                    // remove file in queue
                    upsertStatus(blobString, -1);
                });
        },
        [upsertStatus, lang, propertyId],
    );

    return {
        queue: uploadQueue,
        upload,
    };
};
